/** @module OneHatAudio */

import EventEmitter from '@onehat/events';
import { TaskTimer } from 'tasktimer';
import _ from 'lodash';
 
/**
 * Cross-platform audio player with extra capabilities like:
 * - karoke mode
 * - timing events to exact points within an audio file
 * 
 * @extends EventEmitter
 */
export default class Player extends EventEmitter {

	constructor(config = {}) {
		super(...arguments);

		const defaults = {

			/**
			 * Volume of player. Float from 0.0 to 1.0.
			 * NOTE: when using web api, volume takes the physical mute button into account!!
			 * @member
			 */
			volume: 1,
			
			pulseInterval: 100, // ms

			baseURL: '',

			headers: null,

			/**
			 * @member {boolean} debugMode - Whether this Repository should output debug messages
			 */
			debugMode: false,

			/**
			 * @member {boolean} usePulser - Whether to use the built-in timer
			 */
			usePulser: true,

			/**
			 * @member {boolean} isEnabled - Whether the player can currently play audio
			 */
			isEnabled: true,

			/**
			 * @member {boolean} isKaraoke - Whether the player is currently in karaoke mode
			 */
			isKaraoke: false,

			/**
			 * @member {boolean} isAutoAdvance - Whether the player should emit an event to tell parent containers to advance to the next song
			 */
			isAutoAdvance: false,

			/**
			 * @member {boolean} isLooping - Whether the song restarts when it ends
			 */
			isLooping: true,

		};

		_.merge(this, defaults, config);
		this.originalConfig = config;


		this.registerEvents([
			'load',
			'ready',
			'end',
			'event',
			'play',
			'pause',
			'pulse',
			'next',
			'changeIsKaraoke',
			'changeIsLooping',
			'changeIsDucking',
			'changeIsScrubbing',
			'changeRate',
			'changeIsAutoAdvance',
			'startScrubbing',
			'endScrubbing',
			'error',
		]);


		/**
		 * @member {boolean} isPlaying - Whether the player is currently playing
		 */
		this.isPlaying = false;

		/**
		 * @member {boolean} hasKaraoke - Whether the song has a karaoke version
		 */
		this.hasKaraoke = false;

		/**
		 * @member {boolean} isDucking - Whether or not the player is currently ducking
		 * @private
		 */
		this.isDucking = false;

		/**
		 * @member {boolean} isScrubbing - Whether the player is currently scrubbing to a new location
		 * @private
		 */
		 this.isScrubbing = false;

		/**
		 * @member {boolean} preDuckVolume - Temporary placeholder for volume before it was ducked
		 * @private
		 */
		this.preDuckVolume = 1;
		
		/**
		 * @member {Boolean} isInitialized - State: whether or not this repository has been completely initialized
		 */
		this.isInitialized = false;

		/**
		 * @member {Boolean} isLoading - State: whether or not entities are currently being loaded
		 */
		this.isLoading = false;

		/**
		 * @member {Boolean} isLoaded - State: whether or not entities have been loaded at least once
		 */
		this.isLoaded = false;

		/**
		 * @member {Boolean} wasPreviouslyPlaying - State: whether or not the player was playing before some action occurred
		 */
		this.wasPreviouslyPlaying = false;

		/**
		 * @member {string} appState - State: The current state of the app
		 */
		this.appState = 'active';

		/**
		 * @member {string} pulserActive - State: The current state of the pulser
		 */
		this.pulserActive = true;
		
		/**
		 * @member {boolean} isDestroyed - Whether this object has been destroyed
		 * @private
		 */
		this.isDestroyed = false;


		this.song = null;
		this.songSrc = null;
		this.songKaraoke = null;
		this.songSrcKaraoke = null;
		this.extension = null;
		this.audioEvents = [];

	}

	/**
	 * Initializes the Player.
	 * Subclasses must call super.initialize();
	 */
	initialize(args) {

		// Setup pulse ticker
		if (this.usePulser) {
			this.pulser = new TaskTimer(this.pulseInterval);
			this.pulser.add([{
				id: 'pulse',
				tickInterval: 1,
				totalRuns: 0,
				callback: async () => {
					const pulse = await this.getPulse();
					this.emit('pulse', pulse);
	
					this.emitEvents(pulse);
				},
			}]);
			this.pulser.start();
			this.pulser.pause();
		}
		
		this.isInitialized = true;
	}

	/**
	 * Sets the current *song* in both normal and karaoke versions.
	 * NOTE: A single argument is supplied with the following params:
	 * @param {string} vox - File (type depends on player implementation, could be uri, binary, or other) of "normal" song version with vocals
	 * @param {string} novox - File of "karaoke" song version without vocals
	 * @param {aray} events - Array of events to fire at various points in song.
	 * Each item in array has:
	 * 	- {float} time - Float number denoting seconds since beginning of audio file.
	 * 	- {string} data - Data associated with event
	 * 	- {integer} type - The event type: EVENT_TYPE_QUESTION, EVENT_TYPE_LYRIC, EVENT_TYPE_CLEAR,
	 */
	async setSong({ vox, novox, events }) {
		if (this.debugMode) {
			console.log('Player.setSong', { vox, novox, events });
		}

		this.isLoaded = false;
		this.hasKaraoke = !!novox;
		this.songSrc = null;
		this.songSrcKaraoke = null;

		await this.initSong(vox);
		if (novox) {
			await this.initSong(novox, true);
		}
		this.setIsLoaded();
		this.audioEvents = _.sortBy(events, [(event) => { return parseFloat(event.time); }]);
	}

	/**
	 * Polls and sets whether all songs have loaded.
	 * @private
	 */
	setIsLoaded = () => {
		let isLoaded = true;
		if (!this.songSrc ||
			(this.hasKaraoke && !this.songSrcKaraoke)) {
			isLoaded = false;
		}
		this.isLoaded = isLoaded;
		if (isLoaded) {
			this.emit('load');
		}
		return isLoaded;
	}

	/**
	 * Init song with adapter.
	 * @param {array} files - Array of filenames that represent the same song in different formats (e.g. 'test.mp3', 'test.aac', 'test.mp4')
	 * @param {boolean} isKaraoke - Whether file is karaoke version.
	 * @private
	 * @abstract
	 */
	initSong(files, isKaraoke = false) {
		throw new Error('Abstract method initSong() must be overridden by adapter');
	}

	/**
	 * Compare submitted file with list of playable formats.
	 * If it's playable, return true, else false.
	 * @private
	 * @param {string} file
	 * @return {boolean}
	 */
	canPlayFile = (file) => {
		const matches = file.match(/\.([\w]{3})$/);
		if (!matches[1]) {
			return false;
		}
		const extension = matches[1],
			works = _.find(this.formats, (format) => format === extension);
		return !!works;
	}

	/**
	 * Plays current song.
	 * Can be overridden by subclass if API doesn't work.
	 */
	play = async () => {
		if (this.debugMode) {
			console.log('Player.play');
		}

		await this.playSource();
		this.isPlaying = true;
		this.emit('play');
		if (this.usePulser && this.pulserActive) {
			this.pulser.resume();
		}
		const pulse = await this.getPulse();
		this.emit('pulse', pulse);
	}

	/**
	 * Plays current song in player.
	 * @abstract
	 */
	playSource = () => {
		throw new Error('Abstract method playSource() must be overridden by adapter');
	}

	/**
	 * Pauses current song.
	 * Can be overridden by subclass if API doesn't work.
	 */
	pause = async () => {
		if (this.debugMode) {
			console.log('Player.pause');
		}

		await this.pauseSource();
		this.isPlaying = false;
		this.emit('pause');
		if (this.usePulser && this.pulserActive) {
			this.pulser.pause();
		}
		const pulse = await this.getPulse();
		this.emit('pulse', pulse);
	}

	/**
	 * Pauses current song in player.
	 * @abstract
	 */
	pauseSource = () => {
		throw new Error('Abstract method pauseSource() must be overridden by adapter');
	}

	/**
	 * Puts player into scrubbing mode.
	 * Audio pauses until endScrubbing() is called.
	 */
	startScrubbing = async () => {
		if (!this.isScrubbing) {
			this.isScrubbing = true;
			this.wasPreviouslyPlaying = this.isPlaying;
			await this.pause();
			this.emit('startScrubbing');
		}
	}

	/**
	 * Final percentage where user let go of slider knob.
	 * Set the song to this location and continue from there.
	 * @param {float} percentage - Value from 0 to 100.
	 */
	endScrubbing = async (percentage) => {
		if (this.isScrubbing) {
			this.isScrubbing = false;
			await this.scrubToPercentage(percentage);
			await this.reemitMostRecentEvent();
			if (this.wasPreviouslyPlaying) {
				await this.play();
			} else {
				await this.pause();
			}
			this.emit('endScrubbing');
		}
	}

	/**
	 * Scrubs current song to new time.
	 * @abstract
	 */
	scrub = (time) => {
		throw new Error('Abstract method scrub() must be overridden by adapter');
	}

	/**
	 * Scrubs to a percentage of the song.
	 * @param {float} percentage - Value from 0 to 100.
	 */
	scrubToPercentage = async (percentage) => {
		const duration = await this.getCurrentDuration();
		const seconds = percentage / 100 * duration;
		await this.scrub(seconds);
	}

	/**
	 * Toggles karaoke mode.
	 */
	toggleIsKaraokeMode = async () => {
		const isKaraoke = !this.isKaraoke;

		if (this.debugMode) {
			console.log('Player.toggleIsKaraokeMode', isKaraoke);
		}

		if (this.isPlaying) {
			const currentTime = await this.getCurrentTime();
			await this.pauseSource(); // pause current version
			this.isKaraoke = isKaraoke;
			await this.scrub(currentTime);
			await this.playSource();
		} else {
			this.isKaraoke = isKaraoke;
		}
		this.emit('changeIsKaraoke', this.isKaraoke);
	}

	/**
	 * Sets karaoke mode.
	 * @param {boolean} isKaraoke - Whether file is karaoke version.
	 */
	setIsKaraoke = (isKaraoke) => {
		if (this.debugMode) {
			console.log('Player.setIsKaraoke', isKaraoke);
		}

		if (this.isKaraoke !== isKaraoke) {
			return this.toggleIsKaraokeMode();
		}
		return true;
	}

	/**
	 * Toggles playback.
	 */
	togglePlayback = () => {
		if (this.debugMode) {
			console.log('Player.togglePlayback', this.isPlaying ? 'pause()' : 'play()');
		}
		return this.isPlaying ? this.pause() : this.play();
	}

	/**
	 * Gets an instananous snapshot of progress through file.
	 * @returns object with three elements:
	 * 		time: current time in seconds,
	 * 		duration: duration of whole file,
	 * 		percentage: percentage completed,
	 */
	async getPulse() {
		const time = await this.getCurrentTime(),
			duration = await this.getCurrentDuration(),
			percentage = (time / duration) * 100,
			pulse = {
				time,
				duration,
				percentage,
			};
		if (this.debugMode) {
			console.log('Player.getPulse', pulse);
		}
		return pulse;
	}

	/**
	 * Gets seek time of currentSrc
	 * @returns {number} - # of seconds
	 * @abstract
	 */
	getCurrentTime = () => {
		throw new Error('Abstract method getCurrentTime() must be overridden by adapter');
	}

	/**
	 * Gets duration of currentSrc
	 * @returns {number} - # of seconds
	 * @abstract
	 */
	getCurrentDuration = () => {
		throw new Error('Abstract method getCurrentDuration() must be overridden by adapter');
	}

	/**
	 * Helper to get the appropriate src file
	 * @private
	 */
	getCurrentSrc = () => {
		return this.isKaraoke ? this.songSrcKaraoke : this.songSrc;
	}

	/**
	 * Helper to get all src files
	 * @private
	 */
	getAllSources = () => {
		return [
			this.songSrc,
			this.songSrcKaraoke,
		];
	}

	/**
	 * Sets global volume.
	 * @param {float} volume - Float number between 0.0 and 1.0.
	 * @abstract
	 */
	setVolume = (volume) => {
		throw new Error('Abstract method setVolume() must be overridden by adapter');
	}

	/**
	 * Duck the volume.
	 */
	duck = () => {
		if (this.isDucking) {
			return; // skip if we're already ducking
		}
		this.isDucking = true;
		this.preDuckVolume = this.volume;
		this.setVolume(0.25);
		this.emit('changeIsDucking', true);
	}

	/**
	 * Restore the volume after ducking.
	 */
	unduck = () => {
		if (!this.isDucking) {
			return; // skip if we're already not ducking
		}
		this.isDucking = false;
		this.setVolume(this.preDuckVolume);
		this.emit('changeIsDucking', false);
	}

	/**
	 * Sets playback rate of all files.
	 * @param float playback rate from 0.5 to 4.0, with 1.0 being normal speed.
	 */
	setRate = async (rate) => {
		if (this.debugMode) {
			console.log('Player.setRate', rate);
		}
		this.rate = rate;
		const sources = this.getAllSources();
		let i, src;
		for (i = 0; i < sources.length; i++) {
			src = sources[i];
			await this._setRate(src, rate);
		}
		this.emit('changeRate', rate);
	}

	/**
	 * Directly set playback rate of one file.
	 * @private
	 * @abstract
	 */
	_setRate = (src, rate) => {
		throw new Error('Abstract method _setRate() must be overridden by adapter');
	}

	/**
	 * Sets whether this file loops.
	 * @param {boolean} bool - whether or not to loop
	 */
	setIsLooping = async (bool) => {
		if (this.debugMode) {
			console.log('Player.setIsLooping', bool);
		}
		this.isLooping = bool;
		const sources = this.getAllSources();
		let i, src;
		for (i = 0; i < sources.length; i++) {
			src = sources[i];
			await this._setIsLooping(src, bool);
		}
		if (this.isLooping) {
			this.setIsAutoAdvance(false);
		}
		this.emit('changeIsLooping', bool);
	}

	/**
	 * Directly set whether this file loops.
	 * @private
	 * @abstract
	 */
	_setIsLooping = (src, bool) => {
		throw new Error('Abstract method _setIsLooping() must be overridden by adapter');
	}

	/**
	 * Toggles looping.
	 */
	 toggleIsLooping = () => {
		if (this.debugMode) {
			console.log('Player.toggleIsLooping');
		}
		const isLooping = !this.isLooping;
		this.setIsLooping(isLooping);
	}

	/**
	 * Sets whether this file loops.
	 * @param {boolean} bool - whether or not to loop
	 */
	setIsAutoAdvance = async (bool) => {
		if (this.debugMode) {
			console.log('Player.setIsAutoAdvance', bool);
		}
		this.isAutoAdvance = bool;
		this.emit('changeIsAutoAdvance', bool);

		if (this.isAutoAdvance) {
			this.setIsLooping(false);
		}
	}

	/**
	 * Toggles whether this player emits an event to advance to the next song.
	 */
	toggleIsAutoAdvance = () => {
		if (this.debugMode) {
			console.log('Player.toggleIsAutoAdvance');
		}
		const isAutoAdvance = !this.isAutoAdvance;
		this.setIsAutoAdvance(isAutoAdvance);
	}

	/**
	 * Emits any events that should be fired in the current pulse.
	 */
	emitEvents = async (pulse) => {
		if (this.debugMode) {
			console.log('Player.emitEvents');
		}
		let timeS;
		if (pulse) {
			timeS = pulse.time;
		} else {
			timeS = await this.getCurrentTime();
		}

		const timeMs = timeS * 1000;
		const lag = (this.pulseInterval * this.rate) / 2, // Half it. This shifts the interval range to be centered on the pulse, not at the end of the pulse
			earlyThreshold = timeMs - lag,
			lateThreshold = timeMs + lag;
		_.each(this.audioEvents, (event) => {
			const eventTime = parseFloat(event.time) * 1000;
			if (eventTime >= earlyThreshold && eventTime < lateThreshold) {
				this.emit('event', event);
			}
		});
	}

	/**
	 * Emits the single most recent event.
	 */
	reemitMostRecentEvent = async () => {
		if (this.debugMode) {
			console.log('Player.reemitMostRecentEvent');
		}
		const event = await this.findMostRecentEvent();
		if (event) {
			this.emit('event', event);
		}
	}

	/**
	 * Helper for reemitMostRecentEvent()
	 * Finds and returns the single most recent event.
	 * @private
	 */
	findMostRecentEvent = async () => {
		const time = await this.getCurrentTime(),
			events = this.audioEvents;

		_.reverse(events);
		const found = _.find(events, (event) => {
			return parseFloat(event.time) <= time;
		})
		_.reverse(events);
		return found;
	}

	/**
	 * Internal function to clean up things after a src file plays
	 * @private
	 */
	onEnd = async () => {
		if (this.debugMode) {
			console.log('Player.onEnd');
		};
		
		let stopIt = false;
		if (this.isLooping) {
			// do nothing
		} else if (this.isAutoAdvance) {
			this.wasPreviouslyPlaying = this.isPlaying;
			this.emit('next');
			stopIt = false;
		} else {
			stopIt = true;
		}
		if (stopIt) {
			this.isPlaying = false;
			this.emit('end');
			if (this.usePulser && this.pulserActive) {
				this.pulser.pause();
			}
			await this.scrub(0); // rewind to beginning
			const duration = await this.getCurrentDuration();
			this.emit('pulse', {
				time: 0,
				duration,
				percentage: 0,
			});
		}
	}

	/**
	 * Event handler, called by external object.
	 * Tells the player that the app's state has changes,
	 * and allows it to prepare for that state change.
	 * @param {string} appState - Enumerated list of app states: 'active', 'inactive', 'background'
	 */
	handleAppStateChange = (appState) => {
		this.appState = appState;
		switch(appState) {
			case 'active':
				this.setAppStateToActive();
				break;
			case 'inactive':
				this.setAppStateToInactive();
				break;
			case 'background':
				this.setAppStateToBackground();
				break;
		}
	}

	/**
	 * Helper for handleAppStateChange
	 * For returning to app's 'active' mode
	 * @private
	 */
	setAppStateToActive() {
		if (this.debugMode) {
			console.log('Player.setAppStateToActive');
		};
		if (this.isPlaying) {
			if (this.usePulser && this.pulserActive) {
				this.pulser.resume();
			}
			this.reemitMostRecentEvent();
		}
	}
	
	/**
	 * Helper for handleAppStateChange
	 * For changing to app's 'inactive' mode
	 * @private
	 */
	setAppStateToInactive() {
		if (this.debugMode) {
			console.log('Player.setAppStateToInactive');
		};
		
		if (this.isPlaying) {
			if (this.usePulser && this.pulserActive) {
				this.pulser.pause();
			}
		}
	}
	
	/**
	 * Helper for handleAppStateChange
	 * For changing to app's 'background' mode
	 * @private
	 */
	setAppStateToBackground() {
		if (this.debugMode) {
			console.log('Player.setAppStateToBackground');
		};
		
		if (this.isPlaying) {
			if (this.usePulser && this.pulserActive) {
				this.pulser.pause();
			}
		}
	}

	setPulserActive = () => {
		if (this.debugMode) {
			console.log('Player.setPulserActive');
		};
		
		this.pulserActive = true;
		if (this.usePulser && this.isPlaying) {
			this.pulser.resume();
		}

	}
	setPulserInactive = () => {
		if (this.debugMode) {
			console.log('Player.setPulserInactive');
		};
		
		this.pulserActive = false;
		if (this.usePulser && this.isPlaying) {
			this.pulser.pause();

		}
	}
 
	/**
	 * Destroy this object.
	 * - Removes child objects
	 * - Removes event listeners
	 * @member
	 * @fires destroy
	 */
	destroy() {
		if (this.debugMode) {
			console.log('Player.destroy');
		};
		// child objects

		this.isDestroyed = true;
		
		// listeners
		this.removeAllListeners();
	}

};